export const filmPageData = {
  stats: [
    { value: "3×", label: "Faster Equipment Sourcing" },
    { value: "40%", label: "Production Cost Savings" },
    { value: "100%", label: "Digital Order Management" },
    { value: "48h", label: "Financing Approval Time" },
  ],
  statsAccent: "from-purple-600 to-pink-600",

  challengeTitle: "Film Production Procurement Is Fragmented, Expensive, and Risky",
  challengeSubtitle:
    "Producers, equipment suppliers, and finance providers operate in silos — creating costly delays and missed opportunities throughout the production lifecycle.",
  challenges: [
    {
      icon: "🎬",
      title: "Equipment Sourcing Takes Weeks",
      description:
        "Finding, comparing, and booking production equipment requires weeks of calls, emails, and negotiations — delaying productions.",
    },
    {
      icon: "💰",
      title: "Cash Flow Gaps Between Productions",
      description:
        "Irregular revenue cycles create cash flow gaps that prevent producers from pre-booking equipment and suppliers.",
    },
    {
      icon: "📋",
      title: "Manual Budget Tracking",
      description:
        "Tracking production costs across dozens of suppliers and categories manually leads to budget overruns and financial surprises.",
    },
    {
      icon: "📦",
      title: "Equipment Inventory Blind Spots",
      description:
        "Equipment suppliers lack real-time inventory visibility, leading to double-bookings and last-minute availability failures.",
    },
    {
      icon: "🏦",
      title: "Traditional Financing Doesn't Fit Film",
      description:
        "Banks struggle to underwrite production assets and receivables — leaving productions chronically underfunded.",
    },
    {
      icon: "🔗",
      title: "Disconnected Ecosystem",
      description:
        "Producers, equipment suppliers, and finance providers have no shared digital infrastructure — everyone works in isolation.",
    },
  ],
  theme: {
    challengeBg: "bg-slate-900",
    challengeTitle: "text-white",
    challengeSubtitle: "text-slate-400",
    challengeCard: "bg-slate-800/60",
    challengeBorder: "border-slate-700",
    badgeBg: "bg-purple-50 text-purple-700 border-purple-100",
    solutionIconBg: "bg-purple-50",
    solutionIconColor: "text-purple-600",
    solutionAccent: "from-purple-600 to-pink-600",
  },

  solutionTitle: "One AI Platform Connecting the Entire Film Production Ecosystem",
  solutionSubtitle:
    "Producers, equipment suppliers, and finance providers — all connected, automated, and empowered on EkkBaz AI.",
  solutions: [
    {
      icon: "🎬",
      title: "Digital Equipment Marketplace",
      description:
        "Browse and book verified film equipment suppliers in minutes — with real-time availability, pricing, and reviews.",
    },
    {
      icon: "🤖",
      title: "AI Production Budget Management",
      description:
        "Automate production procurement workflows, track spend in real time, and prevent budget overruns automatically.",
    },
    {
      icon: "💳",
      title: "Embedded Production Financing",
      description:
        "Access B2B financing options — inventory credit, equipment rental financing, invoice factoring — approved in 48 hours.",
    },
    {
      icon: "📊",
      title: "Equipment Inventory Management",
      description:
        "Suppliers manage real-time inventory, availability calendars, and booking schedules in one digital platform.",
    },
    {
      icon: "💰",
      title: "Inventory Financing for Suppliers",
      description:
        "Equipment suppliers access working capital financing to keep stock replenished and revenue flowing between productions.",
    },
    {
      icon: "🔌",
      title: "Finance Provider Integration",
      description:
        "Finance providers embed their products into the procurement workflow — increasing adoption and reducing underwriting risk.",
    },
    {
      icon: "📋",
      title: "Digital Invoice Management",
      description:
        "Automated invoice generation, tracking, and reconciliation for every production transaction — no manual paperwork.",
    },
    {
      icon: "🛡️",
      title: "Verified Ecosystem Participants",
      description:
        "All producers, suppliers, and lenders on the platform are verified — creating a trusted, fraud-resistant marketplace.",
    },
    {
      icon: "📈",
      title: "Production Analytics",
      description:
        "Comprehensive spend analytics and performance reporting for every production — enabling smarter budget decisions.",
    },
  ],

  howItWorksTitle: "How EkkBaz AI Powers Every Film Production Stakeholder",
  howItWorksSteps: [
    {
      icon: "🎬",
      title: "Producer Sets Up Production",
      description: "Producers create their project profile, set budgets, and start sourcing equipment from verified suppliers.",
    },
    {
      icon: "📦",
      title: "Suppliers List & Manage",
      description: "Equipment suppliers list their full catalog with real-time availability and receive digital bookings.",
    },
    {
      icon: "💳",
      title: "Financing Flows In",
      description: "Finance providers offer embedded credit products to producers and suppliers who qualify instantly.",
    },
    {
      icon: "🚀",
      title: "Production Moves Faster",
      description: "Faster sourcing, better cash flow, and digital management means productions complete on time and on budget.",
    },
  ],

  outcomeTitle: "Film Industry Results That Transform How Productions Work",
  outcomeSubtitle: "What producers, equipment suppliers, and finance providers achieve on EkkBaz AI.",
  outcomes: [
    {
      value: "3×",
      metric: "Faster Equipment Sourcing",
      description:
        "Digital marketplace and AI matching reduce equipment sourcing time from weeks to days — keeping productions on schedule.",
    },
    {
      value: "40%",
      metric: "Production Cost Savings",
      description:
        "AI procurement automation and multi-supplier pricing comparisons deliver 40% savings on average production procurement costs.",
    },
    {
      value: "48h",
      metric: "Financing Turnaround",
      description:
        "Embedded financing with AI credit assessment approves production financing in 48 hours — not weeks.",
    },
  ],
  outcomeImage: "/assets/images/producer_banner.png",

  ctaTitle: "Ready to Transform How You Work in Film Production?",
  ctaSubtitle:
    "Whether you're a producer, equipment supplier, or finance provider — EkkBaz AI has a solution for your role in the production ecosystem.",
  ctaLabel: "Book a Free Consultation",
  ctaLink: "https://outlook.office.com/book/EkkBazInvestment@EkkBaz.com/?ismsaljsauthenabled",
};

export const filmFacilites = [
  {
    id: 1,
    userType: "Film Producers",
    description: {
      line1:
        "Managing production budgets, equipment rentals, and supplier payments across a film production is complex and time-consuming. Cash flow gaps between pre-production and distribution are a constant pressure.",
      line2:
        "EkkBaz AI automates your procurement workflow — from equipment sourcing to invoice management — and connects you to embedded financing options so you can focus on creating great content, not chasing payments.",
    },
    btnLabel: "Get Started Free",
    formLink: "https://ekkbaz.com/",
    demoLink: "https://outlook.office.com/book/EkkBazInvestment@EkkBaz.com/?ismsaljsauthenabled",
    demoBtnLabel: "Book a Demo",
    banner: "/assets/images/producer_banner.png",
  },
  {
    id: 2,
    userType: "Film Equipment Suppliers",
    description: {
      line1:
        "Growing a film equipment rental or supply business means juggling hundreds of orders, managing inventory availability, and extending credit to productions that may pay late.",
      line2:
        "With EkkBaz AI, you can list your entire equipment catalog digitally, receive and manage orders in real time, and access inventory financing to keep stock replenished and revenue flowing.",
    },
    btnLabel: "Join as a Supplier",
    formLink: "https://seller.ekkbaz.com/",
    demoLink: "https://outlook.office.com/book/EkkBazInvestment@EkkBaz.com/?ismsaljsauthenabled",
    demoBtnLabel: "Book a Demo",
    banner: "/assets/images/suppler_banner.png",
  },
  {
    id: 3,
    userType: "Finance Providers",
    description: {
      line1:
        "The film and media industry has a significant unmet need for B2B inventory and production financing. Traditional lenders struggle to underwrite production assets and receivables efficiently.",
      line2:
        "EkkBaz AI embeds your financing products directly into the procurement workflow of film producers and equipment suppliers — increasing adoption and reducing default risk through real-time data.",
    },
    btnLabel: "Book a Consultation",
    formLink: "https://outlook.office.com/book/EkkBazInvestment@EkkBaz.com/?ismsaljsauthenabled",
    banner: "/assets/images/money.jpeg",
  },
];

export const filmFAQs = [
  {
    question: "How does EkkBaz AI help film producers manage procurement?",
    answer:
      "EkkBaz AI automates the entire procurement process for film producers — from finding and comparing equipment suppliers to placing orders, tracking deliveries, and managing invoices — saving up to 2.5 hours of manual work daily.",
  },
  {
    question: "Can film equipment suppliers manage their inventory on EkkBaz?",
    answer:
      "Yes. Equipment suppliers can list their full catalog, set pricing and availability in real time, receive digital orders, and manage fulfillment — all from one AI-powered platform.",
  },
  {
    question: "What financing options are available for film productions?",
    answer:
      "EkkBaz AI connects film productions with embedded B2B financing options including inventory credit, equipment rental financing, and invoice factoring through our network of finance providers — with approval in as little as 48 hours.",
  },
];
